import { useState } from 'react';

import { Icon } from '@/new/components/icon';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { TextWithReferences } from '@/new/components/text-references';
import { cn } from '@/new/lib/utils';

interface CaseSummarySectionProps {
  summaryType: 'Subset' | 'Matter';
  summaryText: string;
  isProcessing: boolean;
  isLoading?: boolean;
  hasIssues?: boolean;
}

export const Summary = ({
  summaryType,
  summaryText,
  isProcessing = false,
  isLoading = false,
  hasIssues = false,
}: CaseSummarySectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <div>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full bg-gray-200" />
            <Skeleton className="h-4 w-5/6 bg-gray-200" />
            <Skeleton className="h-4 w-4/5 bg-gray-200" />
          </div>
        ) : isProcessing ? (
          <div className="flex h-full flex-col gap-4">
            <div className="text-sm text-gray-500">The summary is currently being generated.</div>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ) : summaryText ? (
          <div className={cn('w-full rounded-lg border', hasIssues ? 'rounded-b-none' : '')}>
            <div className="flex items-center gap-3 rounded-t-lg border-b bg-gray-50 px-4 py-2.5">
              <Icon className="lucide--file-pen-line h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">{summaryType} Summary</span>
            </div>
            <div className="relative my-4 px-6">
              <div
                className={cn(
                  'prose prose-sm max-w-none leading-relaxed text-gray-700 [&_p]:my-4',
                  !isExpanded && 'line-clamp-2',
                )}
              >
                <TextWithReferences content={summaryText} />
              </div>
              {summaryText && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={cn(
                    'mt-3 flex items-center gap-1.5 text-sm font-medium text-blue-500 transition-colors hover:text-blue-500',
                    isExpanded && 'mt-4',
                  )}
                >
                  <span>{isExpanded ? 'Show less' : 'Show more'}</span>
                  <Icon
                    className={cn('lucide--chevron-down text-xs transition-transform', isExpanded && 'rotate-180')}
                  />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">No summary available</div>
        )}
      </div>
    </>
  );
};
